/**
 * Brief → search plan helpers from MCP one-shot builder.
 * Pure: no DB, no retrieval ports.
 */

export const fold = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

const REGION_STOP: Record<string, true> = {
  the: true,
  and: true,
  for: true,
  area: true,
  region: true,
  valley: true,
  vale: true,
  coast: true,
  country: true,
  wine: true,
  north: true,
  south: true,
  east: true,
  west: true,
  central: true,
  alto: true,
  baixa: true,
};

/** Significant region words. "Douro Valley" → ["douro"]. */
export const regionTokens = (region: string): readonly string[] => {
  const tokens = fold(region)
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length > 2 && !REGION_STOP[token]);
  if (tokens.length > 0) return tokens;
  const whole = fold(region)
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  return whole ? [whole] : [];
};

const HUB_BY_TOKEN: Readonly<Record<string, string>> = {
  douro: 'Porto airport',
  porto: 'Porto airport',
  minho: 'Porto airport',
  algarve: 'Faro airport',
  faro: 'Faro airport',
  lisbon: 'Lisbon airport',
  lisboa: 'Lisbon airport',
  sintra: 'Lisbon airport',
  cascais: 'Lisbon airport',
  madeira: 'Funchal airport',
  funchal: 'Funchal airport',
  azores: 'Ponta Delgada airport',
  acores: 'Ponta Delgada airport',
  alentejo: 'Lisbon airport',
  evora: 'Lisbon airport',
};

export const hubFromRegion = (region: string): string | undefined => {
  const token = fold(region)
    .split(/[^a-z0-9]+/)
    .find((part) => part.length > 2 && HUB_BY_TOKEN[part]);
  return token ? HUB_BY_TOKEN[token] : undefined;
};

const HUB_QUERY_STOP: Record<string, true> = {
  airport: true,
  transfer: true,
  station: true,
};

/** One catalogue token. "Porto airport" → "porto". */
export const hubToken = (hub: string): string | undefined =>
  fold(hub)
    .split(/[^a-z0-9]+/)
    .find((token) => token.length > 2 && !HUB_QUERY_STOP[token]);

const PREFERENCE_STOP: Record<string, true> = {
  the: true,
  and: true,
  one: true,
  with: true,
  for: true,
  want: true,
  wants: true,
  like: true,
  really: true,
  not: true,
  trip: true,
  tour: true,
  day: true,
  days: true,
};

/** Significant preference words. Phrases become single LIKE tokens. */
export const preferenceTokens = (
  preferences: readonly string[] | undefined
): readonly string[] => {
  const seen = new Set<string>();
  const tokens: string[] = [];
  for (const phrase of preferences ?? []) {
    for (const raw of fold(phrase).split(/[^a-z]+/)) {
      if (raw.length < 4 || PREFERENCE_STOP[raw] || seen.has(raw)) continue;
      seen.add(raw);
      tokens.push(raw);
    }
  }
  return tokens;
};

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** "Trip: 2026-04-10 to 2026-04-17, month April 2026". */
export const tripLine = (startOn: string, days: number): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startOn);
  if (!match) return `Trip: ${startOn} to ${startOn}, month ${startOn}`;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const end = new Date(Date.UTC(year, monthIndex, day + Math.max(days, 1) - 1))
    .toISOString()
    .slice(0, 10);
  const month = MONTHS[monthIndex] ?? match[2];
  return `Trip: ${startOn} to ${end}, month ${month} ${year}`;
};

export type RecommendRole = 'hotel' | 'transfer' | 'activity' | 'dining';

export const ROLE_CATEGORIES: Record<RecommendRole, readonly string[]> = {
  hotel: ['Accommodation'],
  transfer: ['Transportation'],
  activity: ['Activity'],
  dining: ['Dining'],
};

export type SearchBrief = {
  readonly country: string;
  readonly region: string;
  readonly preferences?: readonly string[];
  readonly arrivalHub?: string;
};

export type RoleSearch = {
  readonly role: RecommendRole;
  readonly query: string;
  readonly categories: readonly string[];
};

const QUERY_TOKEN_CAP = 3;

const uniqueTokens = (tokens: readonly string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const token of tokens) {
    if (!token || seen.has(token)) continue;
    seen.add(token);
    out.push(token);
  }
  return out;
};

/**
 * One significant token per search. Category and country already scope the
 * role; a second token AND-filters the page down to nothing.
 * A preference is its own search, never concatenated with the region.
 */
export const searchesFor = (input: SearchBrief): readonly RoleSearch[] => {
  const regionToken = regionTokens(input.region)[0];
  const prefs = preferenceTokens(input.preferences).slice(0, QUERY_TOKEN_CAP);
  const scoped = uniqueTokens([
    ...(regionToken ? [regionToken] : []),
    ...prefs,
  ]);
  const roleQueries = scoped.length > 0 ? scoped : [input.region];
  const hub = input.arrivalHub?.trim() || hubFromRegion(input.region);
  const hubQ = hub ? hubToken(hub) : undefined;
  const transferQueries = uniqueTokens([
    ...(hubQ ? [hubQ] : []),
    ...(regionToken ? [regionToken] : []),
  ]);
  const transferSearches: RoleSearch[] =
    transferQueries.length > 0
      ? transferQueries.map((query) => ({
          role: 'transfer',
          query,
          categories: ROLE_CATEGORIES.transfer,
        }))
      : [
          {
            role: 'transfer',
            query: input.region,
            categories: ROLE_CATEGORIES.transfer,
          },
        ];

  const hotelSearches = roleQueries.map((query) => ({
    role: 'hotel' as const,
    query,
    categories: ROLE_CATEGORIES.hotel,
  }));
  const activitySearches = roleQueries.map((query) => ({
    role: 'activity' as const,
    query,
    categories: ROLE_CATEGORIES.activity,
  }));
  const diningSearches = roleQueries.map((query) => ({
    role: 'dining' as const,
    query,
    categories: ROLE_CATEGORIES.dining,
  }));

  return [
    ...hotelSearches,
    ...activitySearches,
    ...diningSearches,
    ...transferSearches,
  ];
};

export const nearestHub = (input: {
  readonly region: string;
  readonly arrivalHub?: string;
}): string | undefined =>
  input.arrivalHub?.trim() || hubFromRegion(input.region);
